import '../../styles/main.less';
import './styles.less';
import { logEvent } from '../../models/Analytics';
import { WebServiceHelper } from '../WebServiceHelper';
import * as React from 'react';

interface IProps {
	className?: string;
	isPersonalPlan?: boolean;
	showPopup?: boolean;
	onClose?: any;
}

interface IState {
	companyName?: string;
	emailAddress?: string;
	errorMessage?: string;
	showConfirmation?: boolean;
	showEmailNotSupported?: boolean;
	showLoader?: boolean;
	showPopup?: boolean;
}

export class RequestInvite extends React.Component<IProps, IState> {
	public state: IState = {
		companyName: null,
		emailAddress: null,
		errorMessage: null,
		showConfirmation: false,
		showEmailNotSupported: false,
		showLoader: false,
		showPopup: this.props.showPopup,
	};

	private closeButtonClick = () => {
		// Clear the error (if any)
		this.setState({errorMessage: null, showLoader: false, showConfirmation: false, showEmailNotSupported: false});

		// Close the window
		this.props.onClose();
	}

	private differentEmailClick = () => {
		logEvent('Signup', 'UseDifferentEmail', null);
		this.setState({errorMessage: null, showLoader: false, showConfirmation: false, showEmailNotSupported: false});
	}

	private updateFormFieldValue = (e: any) => {
		const id: string = e.target.id;
		const value: string = e.target.value;
		let emailAddress: string = this.state.emailAddress;
		let companyName: string = this.state.companyName;

		if (id === 'emailAddress') {
			emailAddress = value;
		} else {
			companyName = value;
		}
		this.setState({emailAddress, companyName});
	}

	private signupClick = () => {
		const email: string = this.state.emailAddress;
		const companyName: string = this.state.companyName;
		const isEmailValid: boolean = !!email && email.length > 0 && email.indexOf('@') > -1 ? true : false;
		const isCompanyValid: boolean = !!companyName && companyName.length > 0;
		if (isEmailValid && isCompanyValid) {
			this.setState({errorMessage: null, showLoader: true});
			// Call web service depending on whether this is a personal or business plan
			if (this.props.isPersonalPlan) {
				logEvent('Signup', 'CreateAccount-Personal', null);
				new WebServiceHelper().createPersonalAccount(companyName, email, this.submitCallback);
			} else {
				logEvent('Signup', 'CreateAccount-Business', null);
				new WebServiceHelper().createFreeTrial(companyName, email, this.submitCallback);
			}
		} else {
			// Display error message
			if (!isEmailValid) {
				this.setState({errorMessage: 'You must provide a valid email address to sign up.'});
			} else {
				// Format company invalid message based on whether this is a personal or business trial
				if (this.props.isPersonalPlan) {
					this.setState({errorMessage: 'You must provide your full name.'});
				} else {
					this.setState({errorMessage: 'Company Name is required.'});
				}
			}
		}
	}

	private showFormContent = () => {
		return !this.state.showConfirmation && !this.state.showEmailNotSupported;
	}

	private formatCompanyNameText = () => {
		if (this.props.isPersonalPlan) {
			return 'Your Full Name';
		} else {
			return 'Company Name';
		}
	}

	private submitCallback = (response: any) => {
		if (response.ok) {
			// Show confirmation message
			this.setState({errorMessage: null, showConfirmation: true, showLoader: false});
		} else if (response.status === 411) {
			// We do not support their email provider
			logEvent('Signup', 'Error-EmailNotSupported', this.state.emailAddress || '');
			this.setState({errorMessage: null, showConfirmation: false, showLoader: false, showEmailNotSupported: true});
		} else if (response.status === 409) {
			// User already has an account
			logEvent('Signup', 'Error-EmailExists', this.state.emailAddress || '');
			const errorMessage: string = 'An account with this email address already exists. If it is your account, go to the sign in page and click Forgot Password to reset your credentials';
			this.setState({showConfirmation: false, showLoader: false, errorMessage});
		} else {
			logEvent('Signup', 'Error-General', response.statusText);
			this.setState({showConfirmation: false, showLoader: false, errorMessage: 'There was a problem submitting your request. Please try again.'});
		}
	}

	public render() {
		return (
			<div className={this.props.showPopup ? 'requestInvitePopupOuterContainer' : 'hide'}>
				<div className='requestInvitePopup-closeButtonContainer'>
					<span className='requestInvitePopup-closeButton' onClick={this.closeButtonClick} />
				</div>
				<div className={this.showFormContent() ? 'signupForm-outerContainer' : 'hide'}>
					<div className='requestInviteCTAHeadline'>
						Sign Up for a Levitate {!!this.props.isPersonalPlan && this.props.isPersonalPlan ? 'Personal' : 'Business'} Account
					</div>
					<div className={this.state.showLoader ? 'loaderSmall' : 'hide'} />
					<div className={!this.state.showLoader ? 'signupForm-formContainer' : 'hide'}>
						<div className={!!this.state.errorMessage ? 'signupForm-errorContainer' : 'hide'}>
							{this.state.errorMessage}
						</div>
						<div className='formFieldContainer'>
							<div className='signupForm-formFieldLabel'>
								{this.formatCompanyNameText()}
							</div>
							<div>
								<input
									type='text'
									id='companyName'
									autoFocus={true}
									className='requestInviteCTAEmailTextbox'
									value={ !!this.state.companyName ? this.state.companyName : '' }
									onChange={this.updateFormFieldValue }
								/>
							</div>
						</div>
						<div className='formFieldContainer'>
							<div className='signupForm-formFieldLabel'>
								{!!this.props.isPersonalPlan && this.props.isPersonalPlan ? 'Your Email Address' : 'Your Work Email'}
							</div>
							<div>
								<input
									type='text'
									id='emailAddress'
									placeholder={this.props.isPersonalPlan ? '' : 'name@yourcompany.com'}
									className='requestInviteCTAEmailTextbox'
									value={ !!this.state.emailAddress ? this.state.emailAddress : '' }
									onChange={this.updateFormFieldValue }
								/>
							</div>
						</div>
						<span className='cta-button-80Percent' onClick={this.signupClick}>
							Sign Up
						</span>
					</div>
				</div>
				<div className={this.state.showConfirmation ? 'signupForm-confirmationContainer' : 'hide'}>
					<div className='requestInviteCTAHeadline'>
						Thank you for signing up!
					</div>
					<div className='signupForm-emailGraphic' />
					<div className='signupForm-confirmationBodyText'>
						We sent an activation link to <strong>{this.state.emailAddress}</strong>.<br />
						We'll see you in the Levitate app!
					</div>
				</div>
				<div className={this.state.showEmailNotSupported ? 'signupForm-confirmationContainer' : 'hide'}>
					<div className='requestInviteCTAHeadline'>
						We don't support your email provider (yet)
					</div>
					<div className='signupForm-emailGraphicError' />
					<div className='signupForm-confirmationBodyText'>
						Currently, Levitate supports Gmail, Google Apps, Office 365, Outlook.com, Live.com, and Hotmail
						email accounts. Exchange support is coming soon.
					</div>
					<div>
						<span className='cta-button' onClick={this.differentEmailClick}>
								Sign up with a different email
						</span>
					</div>
				</div>
			</div>
		);
	}
}
